import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer.js';
import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer.js';
import HeatmapRenderer from '@arcgis/core/renderers/HeatmapRenderer.js';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';

import { useLayerRegistry } from '@/map/layers/registry';
import type { LayerProbe, LegendEntry } from '@/map/layers/types';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  resolveFieldName,
} from './shared/layers';
import {
  geometrySwatchKind,
  rgbToHex,
  symbolFor,
  type ClassificationSymbol,
  type RGB,
} from './shared/classification-symbols';
import type { Tool } from './types';

type Scheme = 'unique-values' | 'class-breaks' | 'heatmap';

type Input = {
  layer_id: string;
  field: string;
  scheme: Scheme;
  breaks?: number;
};

const PALETTE: RGB[] = [
  [33, 160, 162],
  [232, 127, 59],
  [38, 168, 100],
  [210, 65, 80],
  [78, 113, 200],
  [156, 96, 196],
  [180, 140, 50],
  [120, 200, 160],
  [200, 80, 200],
  [60, 160, 220],
];

const RAMP_START: RGB = [240, 240, 230];
const RAMP_END: RGB = [33, 160, 162];

const POINT_TYPES = new Set([
  'point',
  'multipoint',
  'esriGeometryPoint',
  'esriGeometryMultipoint',
]);

const MAX_UNIQUE_VALUES = 50;
const DEFAULT_BREAKS = 5;

export const layerClassify: Tool<Input> = {
  name: 'layer_classify',
  description:
    'Re-symbolise a feature layer by an attribute. `scheme` ∈ unique-values | class-breaks | heatmap (heatmap is point-only). The original renderer is cached so `layer_reset_symbology` can restore it.',
  input_schema: {
    type: 'object',
    properties: {
      layer_id: { type: 'string' },
      field: { type: 'string' },
      scheme: { enum: ['unique-values', 'class-breaks', 'heatmap'] },
      breaks: { type: 'integer', minimum: 2, maximum: 10 },
    },
    required: ['layer_id', 'field', 'scheme'],
    additionalProperties: false,
  },
  execute: async ({ layer_id, field, scheme, breaks }) => {
    const layer = await ensureFeatureLayer(layer_id);
    const record = requireLayerRecord(layer_id);
    const probe = record.probe;

    const resolvedField = resolveFieldName(field, probe, layer);
    if (!resolvedField) {
      throw new Error(
        `layer_classify: field "${field}" not found on layer "${layer_id}"`,
      );
    }

    const geometryType = (layer.geometryType as string | undefined) ?? probe?.service.geometry_type;

    if (scheme === 'heatmap' && !POINT_TYPES.has(String(geometryType))) {
      throw new Error('layer_classify: heatmap is only valid for point layers');
    }

    if (!record.original_renderer) {
      useLayerRegistry.getState().patch(layer_id, {
        original_renderer: layer.renderer ?? undefined,
      });
    }

    const swatchKind = geometrySwatchKind(geometryType);
    let renderer: __esri.Renderer;
    let summary: Record<string, unknown>;
    let legendEntries: LegendEntry[] | undefined;
    if (scheme === 'unique-values') {
      const result = await buildUniqueValuesRenderer(layer, probe, resolvedField, geometryType);
      renderer = result.renderer;
      summary = { unique_values: result.values.length, classes: result.labels };
      legendEntries = swatchKind
        ? result.values.map((v, i) => ({
            index: i,
            label: result.labels[i],
            value: String(v),
            color: rgbToHex(PALETTE[i % PALETTE.length]),
            kind: swatchKind,
          }))
        : undefined;
    } else if (scheme === 'class-breaks') {
      const result = await buildClassBreaksRenderer(
        layer,
        probe,
        resolvedField,
        geometryType,
        breaks ?? DEFAULT_BREAKS,
      );
      renderer = result.renderer;
      summary = { breaks: result.breaks };
      legendEntries = swatchKind
        ? result.breaks.map((b, i) => ({
            index: i,
            label: `${formatNumber(b.min)} – ${formatNumber(b.max)}`,
            color: rgbToHex(
              lerpColor(RAMP_START, RAMP_END, result.breaks.length <= 1 ? 1 : i / (result.breaks.length - 1)),
            ),
            kind: swatchKind,
          }))
        : undefined;
    } else {
      renderer = buildHeatmapRenderer(resolvedField);
      summary = {};
    }

    layer.renderer = renderer;
    useLayerRegistry.getState().patch(layer_id, {
      classified: { field: resolvedField, scheme },
      legend:
        scheme === 'heatmap' || !legendEntries
          ? undefined
          : { field: resolvedField, scheme, entries: legendEntries },
    });

    return {
      ok: true,
      layer_id,
      field: resolvedField,
      scheme,
      ...summary,
    };
  },
};

async function buildUniqueValuesRenderer(
  layer: FeatureLayer,
  probe: LayerProbe | undefined,
  field: string,
  geometryType: string | undefined,
): Promise<{
  renderer: UniqueValueRenderer;
  values: Array<string | number>;
  labels: string[];
}> {
  const values = await collectDistinctValues(layer, probe, field);
  const codedValues = probe?.fields.find((f) => f.name === field)?.coded_values;
  const labels = values.map((v) => {
    const mapped = codedValues?.[String(v)];
    return mapped ?? String(v);
  });
  const uniqueInfos = values.map((v, i) => ({
    value: v,
    label: labels[i],
    symbol: symbolFor(geometryType, PALETTE[i % PALETTE.length]),
  }));
  const renderer = new UniqueValueRenderer({
    field,
    uniqueValueInfos: uniqueInfos as unknown as UniqueValueRenderer['uniqueValueInfos'],
    defaultSymbol: symbolFor(geometryType, [150, 150, 150]) as unknown as UniqueValueRenderer['defaultSymbol'],
    defaultLabel: 'Other',
  });
  return { renderer, values, labels };
}

async function collectDistinctValues(
  layer: FeatureLayer,
  probe: LayerProbe | undefined,
  field: string,
): Promise<Array<string | number>> {
  const probedField = probe?.fields.find((f) => f.name === field);
  if (probedField?.distinct_sample && probedField.distinct_sample.length > 0) {
    return probedField.distinct_sample.slice(0, MAX_UNIQUE_VALUES);
  }
  try {
    const rs = await layer.queryFeatures({
      where: '1=1',
      outFields: [field],
      returnDistinctValues: true,
      returnGeometry: false,
      num: MAX_UNIQUE_VALUES,
      orderByFields: [field],
    });
    const out: Array<string | number> = [];
    const seen = new Set<string>();
    for (const f of rs.features) {
      const raw = f.attributes?.[field];
      if (raw == null) continue;
      const key = String(raw);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(typeof raw === 'number' ? raw : String(raw));
      if (out.length >= MAX_UNIQUE_VALUES) break;
    }
    return out;
  } catch {
    throw new Error(
      `layer_classify(unique-values): could not enumerate distinct values for "${field}"`,
    );
  }
}

async function buildClassBreaksRenderer(
  layer: FeatureLayer,
  probe: LayerProbe | undefined,
  field: string,
  geometryType: string | undefined,
  breaks: number,
): Promise<{ renderer: ClassBreaksRenderer; breaks: Array<{ min: number; max: number }> }> {
  const { min, max } = await fieldRange(layer, probe, field);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    throw new Error(
      `layer_classify(class-breaks): could not derive a numeric range for field "${field}"`,
    );
  }
  const step = (max - min) / breaks;
  const infos: Array<{ minValue: number; maxValue: number; symbol: ClassificationSymbol | undefined; label: string }> = [];
  const ranges: Array<{ min: number; max: number }> = [];
  for (let i = 0; i < breaks; i++) {
    const lo = min + step * i;
    const hi = i === breaks - 1 ? max : min + step * (i + 1);
    const t = breaks <= 1 ? 1 : i / (breaks - 1);
    const color = lerpColor(RAMP_START, RAMP_END, t);
    infos.push({
      minValue: lo,
      maxValue: hi,
      symbol: symbolFor(geometryType, color),
      label: `${formatNumber(lo)} – ${formatNumber(hi)}`,
    });
    ranges.push({ min: lo, max: hi });
  }
  const renderer = new ClassBreaksRenderer({
    field,
    classBreakInfos: infos as unknown as ClassBreaksRenderer['classBreakInfos'],
    defaultSymbol: symbolFor(geometryType, [150, 150, 150]) as unknown as ClassBreaksRenderer['defaultSymbol'],
    defaultLabel: 'Other',
  });
  return { renderer, breaks: ranges };
}

async function fieldRange(
  layer: FeatureLayer,
  probe: LayerProbe | undefined,
  field: string,
): Promise<{ min: number; max: number }> {
  const probedField = probe?.fields.find((f) => f.name === field);
  if (
    probedField &&
    typeof probedField.min === 'number' &&
    typeof probedField.max === 'number'
  ) {
    return { min: probedField.min, max: probedField.max };
  }
  const rs = await layer.queryFeatures({
    where: '1=1',
    outStatistics: [
      { statisticType: 'min', onStatisticField: field, outStatisticFieldName: '_min' },
      { statisticType: 'max', onStatisticField: field, outStatisticFieldName: '_max' },
    ],
  });
  const row = rs.features[0]?.attributes ?? {};
  return { min: Number(row._min), max: Number(row._max) };
}

function buildHeatmapRenderer(field: string): HeatmapRenderer {
  return new HeatmapRenderer({
    field,
    radius: 18,
    maxDensity: 0.04,
    minDensity: 0,
    colorStops: [
      { ratio: 0, color: 'rgba(255, 255, 255, 0)' },
      { ratio: 0.2, color: 'rgba(255, 240, 200, 0.7)' },
      { ratio: 0.5, color: 'rgba(232, 127, 59, 0.85)' },
      { ratio: 0.8, color: 'rgba(210, 65, 80, 0.95)' },
      { ratio: 1, color: 'rgba(120, 30, 60, 1)' },
    ],
  });
}

function lerpColor(a: RGB, b: RGB, t: number): RGB {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

function formatNumber(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 100) return Math.round(n).toString();
  if (abs >= 1) return n.toFixed(1);
  return n.toFixed(2);
}
